/**
 * ActionStageBadge — the single stage renderer for action cards.
 *
 * Wraps the canonical PipelineStageValueBadge with the extra context an
 * action needs (works for ANY pipelinable stage type — deal pipeline
 * stages, lifecycle stages, custom entity pipelines):
 *
 * - When the stage's object type has MULTIPLE pipelines, the pipeline name
 *   renders alongside the badge so "Commit" is unambiguous ("Commit" in
 *   which pipeline?).
 * - Hovering the badge shows the stage's description (when the schema
 *   defines one).
 *
 * Pipeline/description context comes from the pipelines schema
 * (`usePipelines` — React Query cached, 5 min stale time), matched by the
 * stage's entity_id. When the stage isn't found in the schema (still
 * loading, or a stage the schema doesn't know), the badge renders exactly
 * as before with no name or tooltip.
 */

import { useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { PipelineStageValueBadge } from '@/lib/crm/components/properties/labels/PipelineStageValueBadge';
import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { cx } from '@/utils/cx';

export interface ActionStageBadgeProps {
  /** Stage entity_id (UUID). */
  value: string | null | undefined;
  orgId: number | undefined;
  /**
   * Base entity type of the record the stage belongs to (e.g.
   * 'ahoy_contact', 'ahoy_deal'). Scopes the pipelines-schema lookup:
   * stage-only taxonomies (contact lifecycle stages) are NOT included in
   * the unfiltered pipelines listing — they only surface when the endpoint
   * is queried per entity type. Without this, lifecycle stages get no
   * description tooltip. Pass it whenever the call site knows the type.
   */
  entityType?: string;
  /** Fallback label while the CRM store hasn't loaded the stage yet. */
  fallbackLabel?: string;
  /** Size variant, forwarded to PipelineStageValueBadge. */
  size?: 'sm' | 'md';
  /** Muted variant, forwarded to PipelineStageValueBadge. */
  muted?: boolean;
  /**
   * Whether the badge deep-links to the pipeline board (default true).
   * Pass false inside a surface that is itself a click target — a chat row
   * that expands on tap — so the chip never hijacks the tap.
   */
  interactive?: boolean;
  className?: string;
}

interface StageContext {
  pipeline: Pipeline;
  description: string;
  /** True when the stage's object type has more than one pipeline. */
  hasSiblingPipelines: boolean;
}

function findStageContext(
  pipelines: Pipeline[],
  stageEntityId: string
): StageContext | undefined {
  for (const pipeline of pipelines) {
    const stage = pipeline.stages.find(s => s.entity_id === stageEntityId);
    if (!stage) {
      continue;
    }
    // "Multiple pipelines" is scoped to the stage's own object type: two
    // deal pipelines make a deal stage ambiguous; an unrelated task
    // pipeline does not.
    const siblingCount = pipelines.filter(
      p => p.entity_type_name === pipeline.entity_type_name
    ).length;
    return {
      pipeline,
      description: stage.description,
      hasSiblingPipelines: siblingCount > 1,
    };
  }
  return undefined;
}

export function ActionStageBadge({
  value,
  orgId,
  entityType,
  fallbackLabel,
  size,
  muted,
  interactive = true,
  className,
}: ActionStageBadgeProps) {
  const { pipelines } = usePipelines(orgId, entityType);

  const context = useMemo(
    () => (value ? findStageContext(pipelines, value) : undefined),
    [pipelines, value]
  );

  const badge = (
    <PipelineStageValueBadge
      value={value}
      orgId={orgId}
      fallbackLabel={fallbackLabel}
      size={size}
      muted={muted}
      interactive={interactive}
    />
  );

  const pipelineName = context?.hasSiblingPipelines
    ? context.pipeline.label
    : undefined;
  const description = context?.description?.trim();

  if (!pipelineName && !description) {
    return className ? <span className={className}>{badge}</span> : badge;
  }

  const contents = (
    <>
      {badge}
      {pipelineName && (
        <span className='text-xs text-fg-tertiary truncate'>
          {pipelineName}
        </span>
      )}
    </>
  );

  if (!description) {
    return (
      <span
        className={cx('inline-flex items-center gap-1.5 min-w-0', className)}
      >
        {contents}
      </span>
    );
  }

  // The whole stage rendering (badge + pipeline name) is the tooltip
  // trigger. The tooltip's react-aria trigger only attaches hover/focus
  // listeners to a focusable child — Focusable makes the span one (adds
  // tabIndex + interaction props). role='img' is one of the roles
  // react-aria accepts on a Focusable child (the badge is a composite
  // visual); the description is announced via the tooltip's
  // aria-describedby.
  return (
    <Tooltip title={description}>
      <Focusable>
        <span
          role='img'
          aria-label={description}
          className={cx(
            'inline-flex items-center gap-1.5 min-w-0 cursor-pointer',
            className
          )}
        >
          {contents}
        </span>
      </Focusable>
    </Tooltip>
  );
}
